import React, { createRef, PureComponent } from 'react';

import {
  areCropsEqual,
  clamp,
  cls,
  containCrop,
  convertToPercentCrop,
  convertToPixelCrop,
  defaultCrop,
  nudgeCrop,
} from './utils';

import type { Crop, Ords, PercentCrop, PixelCrop, XYOrds } from './types';

interface EVData {
  startClientX: number;
  startClientY: number;
  startCropX: number;
  startCropY: number;
  clientX: number;
  clientY: number;
  isResize: boolean;
  ord?: Ords;
  cropIndex?: number;
}

interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

const DOC_MOVE_OPTS = { capture: true, passive: false };
let instanceCount = 0;

export interface ReactCropProps {
  /** An object of labels to override the built-in English ones */
  ariaLabels?: {
    cropArea: string;
    nwDragHandle: string;
    nDragHandle: string;
    neDragHandle: string;
    eDragHandle: string;
    seDragHandle: string;
    sDragHandle: string;
    swDragHandle: string;
    wDragHandle: string;
  };
  /** The aspect ratio of the crop, e.g. `1` for a square or `16 / 9` for landscape. */
  aspect?: number;
  /** Classes to pass to the `ReactCrop` element. */
  className?: string;
  /** The elements that you want to perform a crop on. For example
   * an image or video. */
  children?: React.ReactNode;
  /** Show the crop area as a circle. If your aspect is not 1 (a square) then the circle will be warped into an oval shape. Defaults to false. */
  circularCrop?: boolean;
  /** Since v10 all crop params are required except for aspect. Omit the entire crop object if you don't want a crop. See README on how to create an aspect crop with a % crop. */
  crop?: Crop;
  /** If true then the user cannot resize or draw a new crop. A class of `ReactCrop--disabled` is also added to the container for user styling. */
  disabled?: boolean;
  /** If true then the user cannot create or resize a crop, but can still drag the existing crop around. A class of `ReactCrop--locked` is also added to the container for user styling. */
  locked?: boolean;
  /** If true is passed then selection can't be disabled if the user clicks outside the selection area. */
  keepSelection?: boolean;
  /** A minimum crop width, in pixels. */
  minWidth?: number;
  /** A minimum crop height, in pixels. */
  minHeight?: number;
  /** A maximum crop width, in pixels. */
  maxWidth?: number;
  /** A maximum crop height, in pixels. */
  maxHeight?: number;
  /** A callback which happens for every change of the crop. You should set the crop to state and pass it back into the library via the `crop` prop. */
  onChange: (crop: PixelCrop, percentageCrop: PercentCrop) => void;
  /** A callback which happens after a resize, drag, or nudge. Passes the current crop state object in pixels and percent. */
  onComplete?: (crop: PixelCrop, percentageCrop: PercentCrop) => void;
  /** A callback which happens when a user starts dragging or resizing. It is convenient to manipulate elements outside this component. */
  onDragStart?: (e: PointerEvent) => void;
  /** A callback which happens when a user releases the cursor or touch after dragging or resizing. */
  onDragEnd?: (e: PointerEvent) => void;
  /** Render a custom element in crop selection. */
  renderSelectionAddon?: (state: ReactCropState) => React.ReactNode;
  /** Show rule of thirds lines in the cropped area. Defaults to false. */
  ruleOfThirds?: boolean;
  /** Inline styles object to be passed to the `ReactCrop` element. */
  style?: React.CSSProperties;
  /** Provide multiple crops to enable multi-crop mode. The component will render and allow interaction with all crops. */
  crops?: Crop[];
  /** Active crop index for multi-crop mode. When undefined, ReactCrop manages the active crop internally. */
  activeCropIndex?: number;
  /** Callback fired when the active crop index changes (multi-crop mode). */
  onActiveCropChange?: (index: number) => void;
  /** Callback fired when any crop changes in multi-crop mode. */
  onChangeCrops?: (
    pixelCrops: PixelCrop[],
    percentCrops: PercentCrop[],
    changedIndex: number,
  ) => void;
  /** Callback fired when a resize/drag completes in multi-crop mode. */
  onCompleteCrops?: (
    pixelCrops: PixelCrop[],
    percentCrops: PercentCrop[],
    changedIndex: number,
  ) => void;
  /** Optional per-crop style overrides when rendering multiple crops. */
  getCropStyleForIndex?: (
    index: number,
    crop: Crop,
  ) => React.CSSProperties | undefined;
  /** Array of aspect ratios for multi-crop mode. If provided, each crop uses its own aspect ratio. Falls back to aspect prop. */
  cropAspects?: Array<number | undefined>;
}

export interface ReactCropState {
  cropIsActive: boolean;
  newCropIsBeingDrawn: boolean;
  activeCropIndex: number | null;
}

export class ReactCrop extends PureComponent<ReactCropProps, ReactCropState> {
  static xOrds = ['e', 'w'];
  static yOrds = ['n', 's'];
  static xyOrds = ['nw', 'ne', 'se', 'sw'];

  static nudgeStep = 1;
  static nudgeStepMedium = 10;
  static nudgeStepLarge = 100;

  static defaultProps = {
    ariaLabels: {
      cropArea: 'Use the arrow keys to move the crop selection area',
      nwDragHandle:
        'Use the arrow keys to move the north west drag handle to change the crop selection area',
      nDragHandle:
        'Use the up and down arrow keys to move the north drag handle to change the crop selection area',
      neDragHandle:
        'Use the arrow keys to move the north east drag handle to change the crop selection area',
      eDragHandle:
        'Use the up and down arrow keys to move the east drag handle to change the crop selection area',
      seDragHandle:
        'Use the arrow keys to move the south east drag handle to change the crop selection area',
      sDragHandle:
        'Use the up and down arrow keys to move the south drag handle to change the crop selection area',
      swDragHandle:
        'Use the arrow keys to move the south west drag handle to change the crop selection area',
      wDragHandle:
        'Use the up and down arrow keys to move the west drag handle to change the crop selection area',
    },
  };

  get document() {
    return document;
  }

  docMoveBound = false;
  mouseDownOnCrop = false;
  dragStarted = false;
  evData: EVData = {
    startClientX: 0,
    startClientY: 0,
    startCropX: 0,
    startCropY: 0,
    clientX: 0,
    clientY: 0,
    isResize: true,
  };

  componentRef = createRef<HTMLDivElement>();
  mediaRef = createRef<HTMLDivElement>();
  resizeObserver?: ResizeObserver;
  initChangeCalled = false;
  instanceId = `rc-${instanceCount++}`;

  state: ReactCropState = {
    cropIsActive: false,
    newCropIsBeingDrawn: false,
    activeCropIndex:
      typeof this.props.activeCropIndex === 'number'
        ? this.props.activeCropIndex
        : null,
  };

  private isMultiMode(props: ReactCropProps = this.props): boolean {
    return Array.isArray(props.crops) && props.crops.length > 0;
  }

  private clampCropIndex(
    index: number | null | undefined,
    props: ReactCropProps = this.props,
  ): number {
    const crops = props.crops ?? [];
    if (!crops.length) {
      return 0;
    }
    const safeIndex = typeof index === 'number' ? index : 0;
    return clamp(safeIndex, 0, crops.length - 1);
  }

  private resolveActiveIndex(
    props: ReactCropProps = this.props,
  ): number | null {
    if (!this.isMultiMode(props)) {
      return null;
    }

    const crops = props.crops ?? [];
    if (!crops.length) {
      return null;
    }

    if (typeof props.activeCropIndex === 'number') {
      return this.clampCropIndex(props.activeCropIndex, props);
    }

    if (typeof this.state.activeCropIndex === 'number') {
      return this.clampCropIndex(this.state.activeCropIndex, props);
    }

    return 0;
  }

  private getActiveCrop(
    props: ReactCropProps = this.props,
    fallbackIndex?: number | null,
  ): Crop | undefined {
    if (this.isMultiMode(props)) {
      const crops = props.crops ?? [];
      if (!crops.length) {
        return undefined;
      }

      let activeIndex: number | null = null;

      if (typeof props.activeCropIndex === 'number') {
        activeIndex = this.clampCropIndex(props.activeCropIndex, props);
      } else if (typeof fallbackIndex === 'number') {
        activeIndex = this.clampCropIndex(fallbackIndex, props);
      } else if (typeof this.state.activeCropIndex === 'number') {
        activeIndex = this.clampCropIndex(this.state.activeCropIndex, props);
      } else {
        activeIndex = 0;
      }

      return crops[activeIndex];
    }

    return props.crop;
  }

  /**
   * Get aspect ratio for a specific crop index.
   * Falls back to the global aspect prop if cropAspects not provided or index out of bounds.
   */
  private getAspectForCrop(
    cropIndex: number | null | undefined,
    props: ReactCropProps = this.props,
  ): number {
    if (
      typeof cropIndex === 'number' &&
      props.cropAspects &&
      cropIndex >= 0 &&
      cropIndex < props.cropAspects.length
    ) {
      const cropAspect = props.cropAspects[cropIndex];
      if (typeof cropAspect === 'number') {
        return cropAspect;
      }
    }
    return props.aspect ?? 0;
  }

  private getCropByIndex(
    index: number | null | undefined,
    props: ReactCropProps = this.props,
  ): Crop | undefined {
    if (this.isMultiMode(props)) {
      const crops = props.crops ?? [];
      if (!crops.length) {
        return undefined;
      }
      const targetIndex = this.clampCropIndex(index, props);
      return crops[targetIndex];
    }

    return props.crop;
  }

  private setActiveCropIndex(nextIndex: number) {
    if (!this.isMultiMode()) {
      return;
    }

    const boundedIndex = this.clampCropIndex(nextIndex);

    if (typeof this.props.activeCropIndex !== 'number') {
      this.setState({ activeCropIndex: boundedIndex });
    }

    if (this.props.onActiveCropChange) {
      this.props.onActiveCropChange(boundedIndex);
    }
  }

  private emitCropChange(
    nextCrop: Crop,
    cropIndex: number | null,
    box: Rectangle,
  ) {
    const { onChange } = this.props;
    const pixelCrop = convertToPixelCrop(nextCrop, box.width, box.height);
    const percentCrop = convertToPercentCrop(nextCrop, box.width, box.height);

    if (onChange) {
      onChange(pixelCrop, percentCrop);
    }

    if (!this.isMultiMode()) {
      return;
    }

    const { crops = [], onChangeCrops } = this.props;
    if (!onChangeCrops || !crops.length) {
      return;
    }

    const targetIndex = this.clampCropIndex(
      cropIndex ?? this.resolveActiveIndex() ?? 0,
    );
    const pixelCrops = crops.map((existing, idx) =>
      idx === targetIndex
        ? pixelCrop
        : convertToPixelCrop(existing, box.width, box.height),
    );
    const percentCrops = pixelCrops.map(existing =>
      convertToPercentCrop(existing, box.width, box.height),
    );

    onChangeCrops(pixelCrops, percentCrops, targetIndex);
  }

  private emitCropComplete(
    nextCrop: Crop,
    cropIndex: number | null,
    box: Rectangle,
    event: PointerEvent | null,
  ) {
    const { onComplete, onDragEnd } = this.props;
    const pixelCrop = convertToPixelCrop(nextCrop, box.width, box.height);
    const percentCrop = convertToPercentCrop(nextCrop, box.width, box.height);

    if (onComplete) {
      onComplete(pixelCrop, percentCrop);
    }

    if (event && onDragEnd) {
      onDragEnd(event);
    }

    if (!this.isMultiMode()) {
      return;
    }

    const { crops = [], onCompleteCrops } = this.props;
    if (!onCompleteCrops || !crops.length) {
      return;
    }

    const targetIndex = this.clampCropIndex(
      cropIndex ?? this.resolveActiveIndex() ?? 0,
    );
    const pixelCrops = crops.map((existing, idx) =>
      idx === targetIndex
        ? pixelCrop
        : convertToPixelCrop(existing, box.width, box.height),
    );
    const percentCrops = pixelCrops.map(existing =>
      convertToPercentCrop(existing, box.width, box.height),
    );

    onCompleteCrops(pixelCrops, percentCrops, targetIndex);
  }

  // We unfortunately get the bounding box every time as x+y changes
  // due to scrolling.
  getBox(): Rectangle {
    const el = this.mediaRef.current;
    if (!el) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    const { x, y, width, height } = el.getBoundingClientRect();
    return { x, y, width, height };
  }

  applyAspectRatiosToInitialCrops() {
    if (!this.isMultiMode()) {
      return;
    }

    const { crops = [], onChangeCrops } = this.props;
    if (!crops.length || !onChangeCrops) {
      return;
    }

    const box = this.getBox();
    if (!box.width || !box.height) {
      return;
    }

    const constrainedCrops = crops.map((crop, index) => {
      const aspect = this.getAspectForCrop(index);

      if (!aspect) {
        return crop;
      }

      // Only apply centering to initial full-size crops (x:0, y:0, width:100, height:100)
      const isInitialCrop =
        crop.x === 0 &&
        crop.y === 0 &&
        crop.width === 100 &&
        crop.height === 100;

      if (!isInitialCrop) {
        // Crop has already been adjusted, don't modify it
        return crop;
      }

      const pixelCrop = convertToPixelCrop(crop, box.width, box.height);

      // Use containCrop directly to apply aspect ratio constraints
      // This is what resizeCrop uses internally
      const constrainedPixel = containCrop(
        pixelCrop,
        aspect,
        'se', // southeast corner
        box.width,
        box.height,
        this.props.minWidth || 0,
        this.props.minHeight || 0,
      );

      // Center the crop within the image bounds
      const centeredPixel = {
        ...constrainedPixel,
        x: (box.width - constrainedPixel.width) / 2,
        y: (box.height - constrainedPixel.height) / 2,
      };

      const result = convertToPercentCrop(centeredPixel, box.width, box.height);
      return result;
    });

    const pixelCrops = constrainedCrops.map(c =>
      convertToPixelCrop(c, box.width, box.height),
    );
    const percentCrops = constrainedCrops as PercentCrop[];
    // Pass -1 to indicate all crops should be updated
    onChangeCrops(pixelCrops, percentCrops, -1);
  }

  applyAspectRatioChanges(prevAspects?: Array<number | undefined>) {
    if (!this.isMultiMode()) {
      return;
    }

    const { crops = [], onChangeCrops, cropAspects } = this.props;
    if (!crops.length || !onChangeCrops || !cropAspects) {
      return;
    }

    const box = this.getBox();
    if (!box.width || !box.height) {
      return;
    }

    // Find which crop's aspect ratio changed
    let changedIndex = -1;
    if (prevAspects && cropAspects) {
      for (let i = 0; i < cropAspects.length; i++) {
        if (prevAspects[i] !== cropAspects[i]) {
          changedIndex = i;
          break;
        }
      }
    }

    // If we couldn't determine which crop changed, don't update
    if (changedIndex === -1) {
      return;
    }

    const crop = crops[changedIndex];
    if (!crop) {
      return;
    }

    const aspect = this.getAspectForCrop(changedIndex);
    if (!aspect) {
      // No aspect constraint, just return
      return;
    }

    const pixelCrop = convertToPixelCrop(crop, box.width, box.height);

    // Apply the new aspect ratio constraint
    const constrainedPixel = containCrop(
      pixelCrop,
      aspect,
      'se', // southeast corner
      box.width,
      box.height,
      this.props.minWidth || 0,
      this.props.minHeight || 0,
    );

    const percentCrop = convertToPercentCrop(
      constrainedPixel,
      box.width,
      box.height,
    );

    // Update only the changed crop
    const updatedCrops = [...crops];
    updatedCrops[changedIndex] = percentCrop;

    const pixelCrops = updatedCrops.map(c =>
      convertToPixelCrop(c, box.width, box.height),
    );
    const percentCrops = updatedCrops as PercentCrop[];

    onChangeCrops(pixelCrops, percentCrops, changedIndex);
  }

  private hasAppliedInitialAspects = false;

  componentDidMount() {
    // Apply aspect ratios when component mounts with crops
    if (
      !this.hasAppliedInitialAspects &&
      this.props.crops &&
      this.props.crops.length > 0
    ) {
      setTimeout(() => {
        this.applyAspectRatiosToInitialCrops();
        this.hasAppliedInitialAspects = true;
      }, 0);
    }
  }

  componentDidUpdate(prevProps: ReactCropProps, prevState: ReactCropState) {
    if (
      !this.hasAppliedInitialAspects &&
      prevProps.crops?.length === 0 &&
      this.props.crops?.length !== 0
    ) {
      // Apply aspect ratios when crops are first set
      setTimeout(() => {
        this.applyAspectRatiosToInitialCrops();
        this.hasAppliedInitialAspects = true;
      }, 0);
    }

    // Check if cropAspects array has changed
    const aspectsChanged =
      this.isMultiMode() &&
      prevProps.cropAspects !== this.props.cropAspects &&
      this.props.crops &&
      this.props.crops.length > 0;

    if (aspectsChanged) {
      // Apply new aspect ratios to existing crops
      this.applyAspectRatioChanges(prevProps.cropAspects);
    }

    const activeCrop = this.getActiveCrop();
    const prevActiveCrop = this.getActiveCrop(
      prevProps,
      prevState.activeCropIndex,
    );

    if (!activeCrop || prevActiveCrop) {
      return;
    }

    const box = this.getBox();
    if (!box.width || !box.height) {
      return;
    }

    const activeIndex = this.resolveActiveIndex();
    this.emitCropComplete(activeCrop, activeIndex, box, null);
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.unbindDocMove();
  }

  bindDocMove() {
    if (this.docMoveBound) {
      return;
    }

    this.document.addEventListener(
      'pointermove',
      this.onDocPointerMove,
      DOC_MOVE_OPTS,
    );
    this.document.addEventListener(
      'pointerup',
      this.onDocPointerDone,
      DOC_MOVE_OPTS,
    );
    this.document.addEventListener(
      'pointercancel',
      this.onDocPointerDone,
      DOC_MOVE_OPTS,
    );

    this.docMoveBound = true;
  }

  unbindDocMove() {
    if (!this.docMoveBound) {
      return;
    }

    this.document.removeEventListener(
      'pointermove',
      this.onDocPointerMove,
      DOC_MOVE_OPTS,
    );
    this.document.removeEventListener(
      'pointerup',
      this.onDocPointerDone,
      DOC_MOVE_OPTS,
    );
    this.document.removeEventListener(
      'pointercancel',
      this.onDocPointerDone,
      DOC_MOVE_OPTS,
    );

    this.docMoveBound = false;
  }

  onCropPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const { disabled } = this.props;
    const box = this.getBox();
    const currentTarget = e.currentTarget as HTMLElement | null;
    const datasetIndex = currentTarget?.dataset?.cropIndex;
    let cropIndex = datasetIndex
      ? Number.parseInt(datasetIndex, 10)
      : this.resolveActiveIndex();
    if (Number.isNaN(cropIndex)) {
      cropIndex = this.resolveActiveIndex();
    }
    const crop = this.getCropByIndex(cropIndex);

    if (!crop) {
      return;
    }

    const pixelCrop = convertToPixelCrop(crop, box.width, box.height);

    if (disabled) {
      return;
    }

    if (e.cancelable) e.preventDefault(); // Stop drag selection.

    // Bind to doc to follow movements outside of element.
    this.bindDocMove();

    // Focus for detecting keypress.
    (this.componentRef.current as HTMLDivElement).focus({
      preventScroll: true,
    });

    const ord = (e.target as HTMLElement).dataset.ord as Ords;
    const isResize = Boolean(ord);
    let startClientX = e.clientX;
    let startClientY = e.clientY;
    let startCropX = pixelCrop.x;
    let startCropY = pixelCrop.y;

    // Set the starting coords to the opposite corner.
    if (ord) {
      const relativeX = e.clientX - box.x;
      const relativeY = e.clientY - box.y;
      let fromCornerX = 0;
      let fromCornerY = 0;

      if (ord === 'ne' || ord == 'e') {
        fromCornerX = relativeX - (pixelCrop.x + pixelCrop.width);
        fromCornerY = relativeY - pixelCrop.y;
        startCropX = pixelCrop.x;
        startCropY = pixelCrop.y + pixelCrop.height;
      } else if (ord === 'se' || ord === 's') {
        fromCornerX = relativeX - (pixelCrop.x + pixelCrop.width);
        fromCornerY = relativeY - (pixelCrop.y + pixelCrop.height);
        startCropX = pixelCrop.x;
        startCropY = pixelCrop.y;
      } else if (ord === 'sw' || ord == 'w') {
        fromCornerX = relativeX - pixelCrop.x;
        fromCornerY = relativeY - (pixelCrop.y + pixelCrop.height);
        startCropX = pixelCrop.x + pixelCrop.width;
        startCropY = pixelCrop.y;
      } else if (ord === 'nw' || ord == 'n') {
        fromCornerX = relativeX - pixelCrop.x;
        fromCornerY = relativeY - pixelCrop.y;
        startCropX = pixelCrop.x + pixelCrop.width;
        startCropY = pixelCrop.y + pixelCrop.height;
      }

      startClientX = startCropX + box.x + fromCornerX;
      startClientY = startCropY + box.y + fromCornerY;
    }

    this.evData = {
      startClientX,
      startClientY,
      startCropX,
      startCropY,
      clientX: e.clientX,
      clientY: e.clientY,
      isResize,
      ord,
      cropIndex: cropIndex ?? undefined,
    };

    this.mouseDownOnCrop = true;
    this.setState({ cropIsActive: true });

    if (cropIndex != null) {
      this.setActiveCropIndex(cropIndex);
    }
  };

  onComponentPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const { disabled, locked, keepSelection } = this.props;
    const box = this.getBox();
    const activeIndex = this.resolveActiveIndex();
    const activeCrop = this.getCropByIndex(activeIndex);
    const hasSelection = this.isMultiMode()
      ? (this.props.crops?.length ?? 0) > 0
      : Boolean(activeCrop);

    if (disabled || locked || (keepSelection && hasSelection)) {
      return;
    }

    if (e.cancelable) e.preventDefault(); // Stop drag selection.

    // Bind to doc to follow movements outside of element.
    this.bindDocMove();

    // Focus for detecting keypress.
    (this.componentRef.current as HTMLDivElement).focus({
      preventScroll: true,
    });

    const cropX = e.clientX - box.x;
    const cropY = e.clientY - box.y;
    const nextCrop: PixelCrop = {
      unit: 'px',
      x: cropX,
      y: cropY,
      width: 0,
      height: 0,
    };

    this.evData = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startCropX: cropX,
      startCropY: cropY,
      clientX: e.clientX,
      clientY: e.clientY,
      isResize: true,
      cropIndex: activeIndex ?? undefined,
    };

    this.mouseDownOnCrop = true;

    this.emitCropChange(nextCrop, activeIndex ?? null, box);

    this.setState({ cropIsActive: true, newCropIsBeingDrawn: true });

    if (activeIndex != null) {
      this.setActiveCropIndex(activeIndex);
    }
  };

  onDocPointerMove = (e: PointerEvent) => {
    const { disabled, onDragStart } = this.props;
    const box = this.getBox();
    const activeIndex = this.evData.cropIndex ?? this.resolveActiveIndex();
    const crop = this.getCropByIndex(activeIndex);

    if (disabled || !crop || !this.mouseDownOnCrop) {
      return;
    }

    // Stop drag selection.
    if (e.cancelable) e.preventDefault();

    if (!this.dragStarted) {
      this.dragStarted = true;
      if (onDragStart) {
        onDragStart(e);
      }
    }

    // Update pointer position.
    const { evData } = this;

    evData.clientX = e.clientX;
    evData.clientY = e.clientY;

    let nextCrop;

    if (evData.isResize) {
      nextCrop = this.resizeCrop();
    } else {
      nextCrop = this.dragCrop();
    }

    if (!areCropsEqual(crop, nextCrop)) {
      this.emitCropChange(nextCrop, activeIndex ?? null, box);
    }
  };

  onComponentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { disabled } = this.props;
    const crop = this.getActiveCrop();

    if (disabled) {
      return;
    }

    const keyCode = e.key;
    let nudged = false;

    if (!crop) {
      return;
    }

    const box = this.getBox();
    const nextCrop = this.makePixelCrop(box);
    const ctrlCmdPressed = navigator.platform.match('Mac')
      ? e.metaKey
      : e.ctrlKey;
    const nudgeStep = ctrlCmdPressed
      ? ReactCrop.nudgeStepLarge
      : e.shiftKey
        ? ReactCrop.nudgeStepMedium
        : ReactCrop.nudgeStep;

    if (keyCode === 'ArrowLeft') {
      nextCrop.x -= nudgeStep;
      nudged = true;
    } else if (keyCode === 'ArrowRight') {
      nextCrop.x += nudgeStep;
      nudged = true;
    } else if (keyCode === 'ArrowUp') {
      nextCrop.y -= nudgeStep;
      nudged = true;
    } else if (keyCode === 'ArrowDown') {
      nextCrop.y += nudgeStep;
      nudged = true;
    }

    if (nudged) {
      if (e.cancelable) e.preventDefault(); // Stop drag selection.

      nextCrop.x = clamp(nextCrop.x, 0, box.width - nextCrop.width);
      nextCrop.y = clamp(nextCrop.y, 0, box.height - nextCrop.height);

      this.emitCropChange(nextCrop, this.resolveActiveIndex(), box);
      this.emitCropComplete(nextCrop, this.resolveActiveIndex(), box, null);
    }
  };

  onHandlerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, ord: Ords) => {
    const {
      disabled,
      minWidth = 0,
      minHeight = 0,
      maxWidth,
      maxHeight,
    } = this.props;
    const activeIndex = this.resolveActiveIndex();
    const aspect = this.getAspectForCrop(activeIndex);
    const crop = this.getActiveCrop();
    const box = this.getBox();

    if (disabled || !crop) {
      return;
    }

    // Keep the event from bubbling up to the container
    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      e.stopPropagation();
      e.preventDefault();
    } else {
      return;
    }

    const ctrlCmdPressed = navigator.platform.match('Mac')
      ? e.metaKey
      : e.ctrlKey;
    const offset = ctrlCmdPressed
      ? ReactCrop.nudgeStepLarge
      : e.shiftKey
        ? ReactCrop.nudgeStepMedium
        : ReactCrop.nudgeStep;

    const pixelCrop = convertToPixelCrop(crop, box.width, box.height);
    const nudgedCrop = nudgeCrop(pixelCrop, e.key, offset, ord);
    const containedCrop = containCrop(
      nudgedCrop,
      aspect,
      ord,
      box.width,
      box.height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
    );

    if (!areCropsEqual(crop, containedCrop)) {
      this.emitCropChange(containedCrop, this.resolveActiveIndex(), box);
      this.emitCropComplete(
        containedCrop,
        this.resolveActiveIndex(),
        box,
        null,
      );
    }
  };

  onDocPointerDone = (e: PointerEvent) => {
    const { disabled } = this.props;
    const box = this.getBox();
    const activeIndex = this.evData.cropIndex ?? this.resolveActiveIndex();
    const crop = this.getCropByIndex(activeIndex);

    this.unbindDocMove();

    if (disabled || !crop) {
      return;
    }

    if (this.mouseDownOnCrop) {
      this.mouseDownOnCrop = false;
      this.dragStarted = false;
      this.emitCropComplete(crop, activeIndex ?? null, box, e);

      this.setState({ cropIsActive: false, newCropIsBeingDrawn: false });
    }
  };

  onDragFocus = (/*e: React.FocusEvent<HTMLDivElement, Element>*/) => {
    // Fixes #491
    this.componentRef.current?.scrollTo(0, 0);
  };

  getCropStyle(crop?: Crop) {
    const targetCrop = crop ?? this.getActiveCrop();

    if (!targetCrop) {
      return undefined;
    }

    return {
      top: `${targetCrop.y}${targetCrop.unit}`,
      left: `${targetCrop.x}${targetCrop.unit}`,
      width: `${targetCrop.width}${targetCrop.unit}`,
      height: `${targetCrop.height}${targetCrop.unit}`,
    };
  }

  dragCrop() {
    const { evData } = this;
    const box = this.getBox();
    const sourceCrop = this.getCropByIndex(evData.cropIndex);
    const nextCrop = this.makePixelCrop(box, sourceCrop);
    const xDiff = evData.clientX - evData.startClientX;
    const yDiff = evData.clientY - evData.startClientY;

    nextCrop.x = clamp(
      evData.startCropX + xDiff,
      0,
      box.width - nextCrop.width,
    );
    nextCrop.y = clamp(
      evData.startCropY + yDiff,
      0,
      box.height - nextCrop.height,
    );

    return nextCrop;
  }

  getPointRegion(
    box: Rectangle,
    origOrd: Ords | undefined,
    minWidth: number,
    minHeight: number,
  ): XYOrds {
    const { evData } = this;

    const relativeX = evData.clientX - box.x;
    const relativeY = evData.clientY - box.y;

    let topHalf: boolean;
    if (minHeight && origOrd) {
      // Uses orig ord (never flip when minHeight != 0)
      topHalf = origOrd === 'nw' || origOrd === 'n' || origOrd === 'ne';
    } else {
      topHalf = relativeY < evData.startCropY;
    }

    let leftHalf: boolean;
    if (minWidth && origOrd) {
      // Uses orig ord (never flip when minWidth != 0)
      leftHalf = origOrd === 'nw' || origOrd === 'w' || origOrd === 'sw';
    } else {
      leftHalf = relativeX < evData.startCropX;
    }

    if (leftHalf) {
      return topHalf ? 'nw' : 'sw';
    } else {
      return topHalf ? 'ne' : 'se';
    }
  }

  resolveMinDimensions(
    box: Rectangle,
    aspect: number,
    minWidth = 0,
    minHeight = 0,
  ) {
    const mw = Math.min(minWidth, box.width);
    const mh = Math.min(minHeight, box.height);

    if (!aspect || (!mw && !mh)) {
      return [mw, mh];
    }

    if (aspect > 1) {
      return mw ? [mw, mw / aspect] : [mh * aspect, mh];
    } else {
      return mh ? [mh * aspect, mh] : [mw, mw / aspect];
    }
  }

  resizeCrop() {
    const { evData } = this;
    const { maxWidth, maxHeight } = this.props;
    const aspect = this.getAspectForCrop(evData.cropIndex);
    const box = this.getBox();
    const [minWidth, minHeight] = this.resolveMinDimensions(
      box,
      aspect,
      this.props.minWidth,
      this.props.minHeight,
    );
    const sourceCrop = this.getCropByIndex(evData.cropIndex);
    let nextCrop = this.makePixelCrop(box, sourceCrop);
    const area = this.getPointRegion(box, evData.ord, minWidth, minHeight);
    const ord = evData.ord || area;
    let xDiff = evData.clientX - evData.startClientX;
    let yDiff = evData.clientY - evData.startClientY;

    // When min dimensions are set, ensure crop isn't dragged when going
    // beyond the other side #554
    if ((minWidth && ord === 'nw') || ord === 'w' || ord === 'sw') {
      xDiff = Math.min(xDiff, -minWidth);
    }

    if ((minHeight && ord === 'nw') || ord === 'n' || ord === 'ne') {
      yDiff = Math.min(yDiff, -minHeight);
    }

    const tmpCrop: PixelCrop = {
      unit: 'px',
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };

    // When aspect is locked, edge handles (n, s, e, w) should not resize
    // Only corner handles (ne, se, sw, nw) can resize with locked aspect
    if (aspect && (ord === 'n' || ord === 's' || ord === 'e' || ord === 'w')) {
      // Return the current crop unchanged
      return nextCrop;
    }

    if (area === 'ne') {
      tmpCrop.x = evData.startCropX;
      tmpCrop.width = xDiff;

      if (aspect) {
        tmpCrop.height = tmpCrop.width / aspect;
        tmpCrop.y = evData.startCropY - tmpCrop.height;
      } else {
        tmpCrop.height = Math.abs(yDiff);
        tmpCrop.y = evData.startCropY - tmpCrop.height;
      }
    } else if (area === 'se') {
      tmpCrop.x = evData.startCropX;
      tmpCrop.y = evData.startCropY;
      tmpCrop.width = xDiff;

      if (aspect) {
        tmpCrop.height = tmpCrop.width / aspect;
      } else {
        tmpCrop.height = yDiff;
      }
    } else if (area === 'sw') {
      tmpCrop.x = evData.startCropX + xDiff;
      tmpCrop.y = evData.startCropY;
      tmpCrop.width = Math.abs(xDiff);

      if (aspect) {
        tmpCrop.height = tmpCrop.width / aspect;
      } else {
        tmpCrop.height = yDiff;
      }
    } else if (area === 'nw') {
      tmpCrop.x = evData.startCropX + xDiff;
      tmpCrop.width = Math.abs(xDiff);

      if (aspect) {
        tmpCrop.height = tmpCrop.width / aspect;
        tmpCrop.y = evData.startCropY - tmpCrop.height;
      } else {
        tmpCrop.height = Math.abs(yDiff);
        tmpCrop.y = evData.startCropY + yDiff;
      }
    }

    const containedCrop = containCrop(
      tmpCrop,
      aspect,
      area,
      box.width,
      box.height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
    );

    // Apply x/y/width/height changes depending on ordinate
    // (fixed aspect always applies both).
    if (aspect || ReactCrop.xyOrds.indexOf(ord) > -1) {
      nextCrop = containedCrop;
    } else if (ReactCrop.xOrds.indexOf(ord) > -1) {
      nextCrop.x = containedCrop.x;
      nextCrop.width = containedCrop.width;
    } else if (ReactCrop.yOrds.indexOf(ord) > -1) {
      nextCrop.y = containedCrop.y;
      nextCrop.height = containedCrop.height;
    }

    // When drawing a new crop with min dimensions we allow flipping, but
    // ensure we don't flip outside the crop area, just ignore those.
    nextCrop.x = clamp(nextCrop.x, 0, box.width - nextCrop.width);
    nextCrop.y = clamp(nextCrop.y, 0, box.height - nextCrop.height);

    return nextCrop;
  }

  renderCropSelection(crop: Crop, index: number, isActive: boolean) {
    const {
      ariaLabels = ReactCrop.defaultProps.ariaLabels,
      disabled,
      locked,
      renderSelectionAddon,
      ruleOfThirds,
    } = this.props;
    const style = this.getCropStyle(crop);
    const customStyle = this.props.getCropStyleForIndex
      ? this.props.getCropStyleForIndex(index, crop)
      : undefined;
    const mergedStyle = customStyle ? { ...style, ...customStyle } : style;
    const selectionClasses = cls(
      'ReactCrop__crop-selection',
      isActive && 'ReactCrop__crop-selection--active',
    );
    const showGuides = ruleOfThirds && (!this.isMultiMode() || isActive);

    if (!style) {
      return undefined;
    }

    return (
      <div
        key={`selection-${index}`}
        style={mergedStyle}
        className={selectionClasses}
        onPointerDown={this.onCropPointerDown}
        aria-label={ariaLabels.cropArea}
        tabIndex={0}
        onKeyDown={this.onComponentKeyDown}
        role="group"
        data-crop-index={index}
      >
        {!disabled && !locked && (
          <div className="ReactCrop__drag-elements" onFocus={this.onDragFocus}>
            <div
              className="ReactCrop__drag-bar ord-n"
              data-ord="n"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-bar ord-e"
              data-ord="e"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-bar ord-s"
              data-ord="s"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-bar ord-w"
              data-ord="w"
              data-crop-index={index}
            />

            <div
              className="ReactCrop__drag-handle ord-nw"
              data-ord="nw"
              tabIndex={0}
              aria-label={ariaLabels.nwDragHandle}
              onKeyDown={e => this.onHandlerKeyDown(e, 'nw')}
              role="button"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-handle ord-n"
              data-ord="n"
              tabIndex={0}
              aria-label={ariaLabels.nDragHandle}
              onKeyDown={e => this.onHandlerKeyDown(e, 'n')}
              role="button"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-handle ord-ne"
              data-ord="ne"
              tabIndex={0}
              aria-label={ariaLabels.neDragHandle}
              onKeyDown={e => this.onHandlerKeyDown(e, 'ne')}
              role="button"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-handle ord-e"
              data-ord="e"
              tabIndex={0}
              aria-label={ariaLabels.eDragHandle}
              onKeyDown={e => this.onHandlerKeyDown(e, 'e')}
              role="button"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-handle ord-se"
              data-ord="se"
              tabIndex={0}
              aria-label={ariaLabels.seDragHandle}
              onKeyDown={e => this.onHandlerKeyDown(e, 'se')}
              role="button"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-handle ord-s"
              data-ord="s"
              tabIndex={0}
              aria-label={ariaLabels.sDragHandle}
              onKeyDown={e => this.onHandlerKeyDown(e, 's')}
              role="button"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-handle ord-sw"
              data-ord="sw"
              tabIndex={0}
              aria-label={ariaLabels.swDragHandle}
              onKeyDown={e => this.onHandlerKeyDown(e, 'sw')}
              role="button"
              data-crop-index={index}
            />
            <div
              className="ReactCrop__drag-handle ord-w"
              data-ord="w"
              tabIndex={0}
              aria-label={ariaLabels.wDragHandle}
              onKeyDown={e => this.onHandlerKeyDown(e, 'w')}
              role="button"
              data-crop-index={index}
            />
          </div>
        )}
        {renderSelectionAddon && (!this.isMultiMode() || isActive) && (
          <div
            className="ReactCrop__selection-addon"
            onPointerDown={e => e.stopPropagation()}
          >
            {renderSelectionAddon(this.state)}
          </div>
        )}
        {showGuides && (
          <>
            <div className="ReactCrop__rule-of-thirds-hz" />
            <div className="ReactCrop__rule-of-thirds-vt" />
          </>
        )}
      </div>
    );
  }

  makePixelCrop(box: Rectangle, cropOverride?: Crop | undefined | null) {
    const sourceCrop = cropOverride ?? this.getActiveCrop();
    const crop = { ...defaultCrop, ...(sourceCrop || {}) };
    return convertToPixelCrop(crop, box.width, box.height);
  }

  render() {
    const {
      aspect,
      children,
      circularCrop,
      className,
      disabled,
      locked,
      style,
      ruleOfThirds,
    } = this.props;
    const { cropIsActive, newCropIsBeingDrawn } = this.state;
    const multiMode = this.isMultiMode();
    const cropArray = multiMode
      ? this.props.crops ?? []
      : this.props.crop
        ? [this.props.crop]
        : [];
    const activeIndex = this.resolveActiveIndex();
    const activeCrop = this.getActiveCrop();
    const hasCrops = cropArray.length > 0;
    const cropSelection = hasCrops
      ? cropArray.map((cropItem, index) =>
          this.renderCropSelection(cropItem, index, index === activeIndex),
        )
      : null;

    const componentClasses = cls(
      'ReactCrop',
      className,
      cropIsActive && 'ReactCrop--active',
      disabled && 'ReactCrop--disabled',
      locked && 'ReactCrop--locked',
      newCropIsBeingDrawn && 'ReactCrop--new-crop',
      activeCrop && aspect && 'ReactCrop--fixed-aspect',
      activeCrop && circularCrop && 'ReactCrop--circular-crop',
      activeCrop && ruleOfThirds && 'ReactCrop--rule-of-thirds',
      !this.dragStarted &&
        activeCrop &&
        !activeCrop.width &&
        !activeCrop.height &&
        'ReactCrop--invisible-crop',
      circularCrop && 'ReactCrop--no-animate',
    );

    return (
      <div ref={this.componentRef} className={componentClasses} style={style}>
        <div
          ref={this.mediaRef}
          className="ReactCrop__child-wrapper"
          onPointerDown={this.onComponentPointerDown}
        >
          {children}
        </div>
        {hasCrops ? (
          <svg className="ReactCrop__crop-mask" width="100%" height="100%">
            <defs>
              <mask id={`hole-${this.instanceId}`}>
                <rect width="100%" height="100%" fill="white" />
                {cropArray.map((maskCrop, index) =>
                  circularCrop ? (
                    <ellipse
                      key={`mask-${index}`}
                      cx={`${maskCrop.x + maskCrop.width / 2}${maskCrop.unit}`}
                      cy={`${maskCrop.y + maskCrop.height / 2}${maskCrop.unit}`}
                      rx={`${maskCrop.width / 2}${maskCrop.unit}`}
                      ry={`${maskCrop.height / 2}${maskCrop.unit}`}
                      fill="black"
                    />
                  ) : (
                    <rect
                      key={`mask-${index}`}
                      x={`${maskCrop.x}${maskCrop.unit}`}
                      y={`${maskCrop.y}${maskCrop.unit}`}
                      width={`${maskCrop.width}${maskCrop.unit}`}
                      height={`${maskCrop.height}${maskCrop.unit}`}
                      fill="black"
                    />
                  ),
                )}
              </mask>
            </defs>
            <rect
              fill="black"
              fillOpacity={0.5}
              width="100%"
              height="100%"
              mask={`url(#hole-${this.instanceId})`}
            />
          </svg>
        ) : undefined}
        {cropSelection}
      </div>
    );
  }
}
